/**
 * Radix-backed modal chrome for the Design tab's dialogs (Hammer, Wizard,
 * Export, Review, etc. — TDD §3.3 recommendation (a): Radix declared directly
 * on crucible-ui). The Anvil dialogs port onto this primitive in Phase 4.
 *
 * Phase 0 also uses this file as the package-boundary gate proof that
 * `@radix-ui/*` resolves and typechecks from within crucible-ui.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

export interface DesignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  /**
   * When false, clicking the overlay (or otherwise interacting outside) does
   * NOT dismiss the modal — the user must use an in-dialog control. Used by
   * the wizard so a stray click off the pane can't abandon a flow. Escape
   * still closes. Default true.
   */
  dismissible?: boolean;
}

export function DesignModal({
  open,
  onOpenChange,
  title,
  children,
  dismissible = true,
}: DesignModalProps): ReactElement {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[min(90vw,720px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-crucible-border bg-crucible-surface p-5 shadow-xl"
          onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <Dialog.Title className="text-sm font-semibold text-crucible-fg">{title}</Dialog.Title>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              data-testid="design-modal-close"
              className="flex items-center gap-1 rounded border border-crucible-border px-2 py-0.5 text-xs text-crucible-muted hover:bg-crucible-bg-elevated"
            >
              <X size={13} />
              Cancel
            </button>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
